import { useState, useRef, useEffect } from 'react';
import { FaSearch, FaBell, FaUserCircle, FaMoon, FaSun, FaUser, FaCog, FaSignOutAlt, FaUserShield } from "react-icons/fa";
import { useTheme } from "./ThemeContext";
import { useAuth } from "./AuthContext";
import UserProfile from "./UserProfile";
import Settings from "./Settings";
import "../header.css";

function Header({ collapsed }) {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [notifications, setNotifications] = useState([
    { id: 1, message: 'New inquiry received', time: '2 min ago', read: false },
    { id: 2, message: 'Product added successfully', time: '1 hour ago', read: false },
    { id: 3, message: 'Blog published', time: '3 hours ago', read: true }
  ]);
  const [showNotifications, setShowNotifications] = useState(false);
  const menuRef = useRef(null);
  const notificationRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getRoleIcon = () => {
    if (user?.role === 'super_admin') return <FaUserShield />;
    if (user?.role === 'admin') return <FaUserShield />;
    return <FaUserCircle />;
  };

  const getRoleBadge = () => {
    const role = user?.role?.replace('_', ' ');
    return <span className={`role-badge-header ${user?.role}`}>{role}</span>;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const updateProfile = (updatedUser) => {
    // Update user in context if needed
    if (user) {
      user.name = updatedUser.name;
      user.email = updatedUser.email;
      user.avatar = updatedUser.avatar;
    }
  };

  return (
    <>
      <div className={`header ${collapsed ? "collapsed" : ""} ${isDarkMode ? "dark" : ""}`}>
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input type="text" placeholder="Search..." />
        </div>

        <div className="header-right">
          {/* Dark Mode Toggle */}
          <div className="icon dark-mode-toggle" onClick={toggleDarkMode}>
            {isDarkMode ? <FaSun /> : <FaMoon />}
          </div>

          {/* Notifications */}
          <div className="icon notification-icon" onClick={() => setShowNotifications(!showNotifications)}>
            <FaBell />
            {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
          </div>

          {/* User Menu */}
          <div className="user-menu-container" ref={menuRef}>
            <div className="user-info" onClick={() => setShowUserMenu(!showUserMenu)}>
              {user?.avatar ? (
                <img src={user.avatar} alt="Avatar" className="user-avatar-small" />
              ) : (
                <div className="user-avatar-placeholder">
                  {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                </div>
              )}
              <div className="user-details-header">
                <span className="user-name-header">{user?.name}</span>
                {getRoleBadge()}
              </div>
            </div>

            {/* User Dropdown Menu */}
            {showUserMenu && (
              <div className="user-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-user-info">
                    {user?.avatar ? (
                      <img src={user.avatar} alt="Avatar" className="dropdown-avatar" />
                    ) : (
                      <div className="dropdown-avatar-placeholder">
                        {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                      </div>
                    )}
                    <div>
                      <div className="dropdown-user-name">{user?.name}</div>
                      <div className="dropdown-user-email">{user?.email}</div>
                    </div>
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                <button onClick={() => { setShowProfile(true); setShowUserMenu(false); }} className="dropdown-item">
                  <FaUser /> My Profile
                </button>
                <button onClick={() => { setShowSettings(true); setShowUserMenu(false); }} className="dropdown-item">
                  <FaCog /> Settings
                </button>
                <div className="dropdown-divider"></div>
                <button onClick={handleLogout} className="dropdown-item logout">
                  <FaSignOutAlt /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div className="notifications-dropdown" ref={notificationRef}>
          <div className="notifications-header">
            <h3>Notifications</h3>
            <button className="mark-all-read">Mark all read</button>
          </div>
          <div className="notifications-list">
            {notifications.map(notif => (
              <div key={notif.id} className={`notification-item ${!notif.read ? 'unread' : ''}`}>
                <div className="notification-content">
                  <p>{notif.message}</p>
                  <span className="notification-time">{notif.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfile && (
        <UserProfile 
          onClose={() => setShowProfile(false)} 
          onUpdateProfile={updateProfile}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <Settings onClose={() => setShowSettings(false)} />
      )}
    </>
  );
}

export default Header;